import { useMemo, type ReactNode } from 'react'
import type { AskUserStagedActionSnapshot } from '@renderer/chat/integrations/ask-user-inline'
import type { ToolCall } from '@renderer/chat/types'
import { useStagedActionsQuery } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'

export interface MiniAskUserActionItem {
  action: AskUserStagedActionSnapshot
  toolCall: ToolCall
}

export interface MiniAskUserActionsState {
  needsInput: boolean
  /** The first pending action's question, so the row says what it needs. */
  prompt?: string
}

function useMiniAskUserActions(items: Array<MiniAskUserActionItem>): MiniAskUserActionsState {
  const { allActions, hasFetched } = useStagedActionsQuery()

  return useMemo(() => {
    const pending = items.find(({ action }) => {
      const liveAction = allActions.find((candidate) => candidate.id === action.id)
      // A row we can't find once the query has answered is gone — answered,
      // archived, or simply not carried by this list — not still waiting.
      // Before that first answer the snapshot is all we have, so a freshly
      // surfaced question still reads as waiting instead of flashing settled.
      if (!liveAction) return !hasFetched
      return liveAction.outputData == null && !liveAction.archivedAt
    })
    return { needsInput: pending != null, prompt: pending?.action.title }
  }, [allActions, hasFetched, items])
}

export function MiniAskUserActionsResolver({
  items,
  children,
}: {
  items: Array<MiniAskUserActionItem>
  children: (state: MiniAskUserActionsState) => ReactNode
}) {
  return children(useMiniAskUserActions(items))
}
